import { Link } from "react-router-dom";
import { Award, BadgeCheck, ShieldCheck, Target, Compass, ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { FinalCTA } from "@/pages/Home";
import { IMG } from "@/data/site";

export default function About() {
  useSEO("About Us", "Cargowyn Logistics Group is a founder-led cross-border freight brokerage in Ontario, built on 7 years of logistics experience and a CIFFA certification.");
  return (
    <div>
      <section className="bg-[#0B192C] text-white pt-24 pb-20 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 noise opacity-[0.05]" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <div className="flex items-center gap-3 mb-5"><span className="h-[2px] w-10 bg-[#FF5A00]" /><span className="font-head uppercase tracking-[0.3em] text-[#FF5A00] text-sm font-700">Who We Are</span></div>
          <h1 className="font-head font-black uppercase text-6xl md:text-8xl tracking-tighter leading-[0.85] max-w-4xl">Built by a broker who answers the phone</h1>
        </div>
      </section>

      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal><div className="border-2 border-[#0B192C] hard-shadow-orange"><img src={IMG.about} alt="Freight logistics team" className="w-full h-[480px] object-cover" /></div></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-head font-black uppercase text-5xl tracking-tighter leading-[0.9]">Our story</h2>
            <p className="text-[#475569] text-lg mt-6 leading-relaxed">After seven years working inside the logistics industry, our founder saw the same problem again and again: shippers stuck with brokers who wouldn't quote straight or pick up the phone. Cargowyn Logistics Group exists to fix that.</p>
            <p className="text-[#475569] text-lg mt-4 leading-relaxed">We started with what we know best — Canada–US cross-border trucking and the oversize, heavy-haul freight most brokers avoid. Backed by a <span className="text-[#0B192C] font-700">CIFFA certification</span> and a vetted carrier network, we handle permits, escorts, and paperwork so your freight keeps moving.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <div className="flex items-center gap-3 border-2 border-[#0B192C] px-5 py-3"><Award className="text-[#FF5A00]" /><span className="font-head font-700 uppercase tracking-wide text-sm">CIFFA Certified</span></div>
              <div className="flex items-center gap-3 border-2 border-[#0B192C]/10 px-5 py-3"><BadgeCheck className="text-[#94A3B8]" /><span className="font-head font-700 uppercase tracking-wide text-sm text-[#94A3B8]">FMCSA Authority — Pending</span></div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-white border-y-2 border-[#0B192C]/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-8">
          {[{ icon: Target, t: "Our Mission", d: "Make cross-border freight simple, transparent and reliable for every shipper we serve." },
            { icon: Compass, t: "Where We're Headed", d: "Phase 2 adds ocean & air forwarding worldwide. Phase 3 brings China–Canada import, customs clearance and warehousing." },
            { icon: ShieldCheck, t: "Our Promise", d: "Honest rates, direct access, and a founder who stakes his name on every load that moves." }].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div className="border-2 border-[#0B192C] p-8 h-full hover:hard-shadow transition-shadow duration-300" data-testid={`about-value-${i}`}>
                <div className="w-14 h-14 bg-[#FF5A00] flex items-center justify-center mb-6"><c.icon className="text-white" size={26} /></div>
                <h3 className="font-head font-800 uppercase text-2xl tracking-tight">{c.t}</h3>
                <p className="text-[#475569] mt-3 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <FinalCTA />
    </div>
  );
}
